import React, { useState } from "react";
import "../../Styles/MahaShakthiTable.css";
import { useNavigate } from "react-router-dom";
import {
  FaBus,
  FaCheckCircle,
  FaRegClock,
  FaSearch,
  FaMapMarkerAlt,
  FaFilter,
  FaSyncAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaTicketAlt,
} from "react-icons/fa";

const INITIAL_DATA = [
  {
    id: 1,
    name: "Kancharla Swarna Latha",
    relation: "D/O Kancharla Nagaiah",
    busPass: "BP-441209",
    route: "Kandukur to Ongole",
    depot: "Kandukur Depot",
    village: "Gudluru",
    mandal: "Gudluru",
    date: "12-06-2026",
    status: "Approved",
  },
  {
    id: 2,
    name: "Mopidevi Padmavathi",
    relation: "W/O Mopidevi Ravi",
    busPass: "BP-552317",
    route: "Ulavapadu to Chirala",
    depot: "Ulavapadu Depot",
    village: "Ulavapadu",
    mandal: "Ulavapadu",
    date: "25-05-2026",
    status: "Pending",
  },
  {
    id: 3,
    name: "Boddu Anitha",
    relation: "D/O Boddu Nageswara Rao",
    busPass: "BP-663428",
    route: "Kandukur to Nellore",
    depot: "Kandukur Depot",
    village: "Kandukur",
    mandal: "Kandukur",
    date: "03-04-2026",
    status: "Approved",
  },
];

const AdminMahaShakthiTable = () => {
  const navigate = useNavigate();

  const [mahashakthiData, setMahashakthiData] = useState(INITIAL_DATA);

  const [searchTerm, setSearchTerm] = useState("");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);

  /* ===================== SEARCH + FILTER LOGIC ===================== */

  const filteredData = mahashakthiData.filter((item) => {
    const matchesSearch =
      searchTerm.trim() === "" ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.relation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.busPass.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesMandal =
      mandalFilter === "All Mandals" || item.mandal === mandalFilter;

    const matchesStatus =
      statusFilter === "All Status" || item.status === statusFilter;

    return matchesSearch && matchesMandal && matchesStatus;
  });

  const handleResetFilters = () => {
    setSearchTerm("");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const totalEntries = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / 10));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * 10;
  const endIndex = startIndex + 10;
  const pageData = filteredData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  /* ===================== EDIT LOGIC ===================== */

  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({ ...item });
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = (e) => {
    e.preventDefault();
    setMahashakthiData((prev) =>
      prev.map((item) => (item.id === editForm.id ? { ...editForm } : item)),
    );
    closeEditModal();
  };

  /* ===================== STATS ===================== */

  const totalBeneficiaries = mahashakthiData.length;
  const approvedCount = mahashakthiData.filter(
    (i) => i.status === "Approved",
  ).length;
  const pendingCount = mahashakthiData.filter(
    (i) => i.status === "Pending",
  ).length;
  const approvedPct =
    totalBeneficiaries > 0
      ? ((approvedCount / totalBeneficiaries) * 100).toFixed(1)
      : 0;
  const pendingPct =
    totalBeneficiaries > 0
      ? ((pendingCount / totalBeneficiaries) * 100).toFixed(1)
      : 0;

  return (
    <div className="ms-page">
      {/* ── FIXED HEADER ── */}
      <div className="ms-header">
        <div className="ms-header-left">
          <div className="ms-header-icon">
            <FaBus />
          </div>
          <div>
            <h2 className="ms-title">Maha Shakthi (Free Bus Travel) Beneficiaries</h2>
            <p className="ms-subtitle">
              Manage and track beneficiaries under Maha Shakthi Scheme
            </p>
          </div>
        </div>

        <button
          className="ms-add-btn"
          onClick={() => navigate("/AdminDashboard/MahaShakthiForm")}
        >
          <span className="ms-add-plus">+</span> Add New Beneficiary
        </button>
      </div>

      {/* ── SCROLLABLE BODY ── */}
      <div className="ms-scroll-body">
        {/* Stat Cards */}
        <div className="ms-stats-row">
          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-purple">
              <FaUserCircle />
            </div>
            <div>
              <p className="ms-stat-label">Total Beneficiaries</p>
              <h3 className="ms-stat-value">{totalBeneficiaries}</h3>
              <p className="ms-stat-sub">All time records</p>
            </div>
          </div>

          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-green">
              <FaCheckCircle />
            </div>
            <div>
              <p className="ms-stat-label">Approved Beneficiaries</p>
              <h3 className="ms-stat-value">{approvedCount}</h3>
              <p className="ms-stat-sub ms-sub-green">{approvedPct}% of total</p>
            </div>
          </div>

          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-orange">
              <FaRegClock />
            </div>
            <div>
              <p className="ms-stat-label">Pending Beneficiaries</p>
              <h3 className="ms-stat-value">{pendingCount}</h3>
              <p className="ms-stat-sub ms-sub-orange">{pendingPct}% of total</p>
            </div>
          </div>

          <div className="ms-stat-card">
            <div className="ms-stat-icon ms-icon-blue">
              <FaTicketAlt />
            </div>
            <div>
              <p className="ms-stat-label">Total Bus Passes Issued</p>
              <h3 className="ms-stat-value">{totalBeneficiaries}</h3>
              <p className="ms-stat-sub ms-sub-blue">All time records</p>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="ms-filter-section">
          <div className="ms-search-wrapper">
            <FaSearch className="ms-search-icon" />
            <input
              type="text"
              placeholder="Search Beneficiary..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="ms-select-wrapper">
            <FaMapMarkerAlt className="ms-select-icon" />
            <select
              value={mandalFilter}
              onChange={(e) => {
                setMandalFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Mandals</option>
              <option>Kandukur</option>
              <option>Gudluru</option>
              <option>Ulavapadu</option>
            </select>
          </div>

          <div className="ms-select-wrapper">
            <FaFilter className="ms-select-icon" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Status</option>
              <option>Approved</option>
              <option>Pending</option>
            </select>
          </div>

          <button className="ms-reset-btn" onClick={handleResetFilters}>
            <FaSyncAlt /> Reset Filters
          </button>
        </div>

        {/* Table */}
        <div className="ms-table-wrapper">
          <table className="ms-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Relation</th>
                <th>Bus Pass No.</th>
                <th>Route</th>
                <th>Depot</th>
                <th>Village</th>
                <th>Mandal</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {pageData.length > 0 ? (
                pageData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <div className="ms-name-cell">
                        <span className="ms-avatar">
                          <FaUserCircle />
                        </span>
                        <span className="ms-name-text">{item.name}</span>
                      </div>
                    </td>
                    <td className="ms-relation-cell">{item.relation}</td>
                    <td>
                      <span className="ms-buspass-badge">{item.busPass}</span>
                    </td>
                    <td>{item.route}</td>
                    <td>{item.depot}</td>
                    <td>
                      <span className="ms-village-cell">
                        <FaMapMarkerAlt /> {item.village}
                      </span>
                    </td>
                    <td>{item.mandal}</td>
                    <td>
                      <span className="ms-date-cell">
                        <FaCalendarAlt /> {item.date}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`ms-status ${
                          item.status === "Approved"
                            ? "ms-approved"
                            : "ms-pending"
                        }`}
                      >
                        {item.status === "Approved" ? (
                          <FaCheckCircle />
                        ) : (
                          <FaRegClock />
                        )}{" "}
                        {item.status}
                      </span>
                    </td>
                    <td className="ms-action-cell">
                      <button
                        className="ms-edit-btn"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit /> Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="ms-no-data">
                    No matching beneficiaries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / Pagination */}
        <div className="ms-footer">
          <p className="ms-footer-text">
            {totalEntries > 0
              ? `Showing ${startIndex + 1} to ${Math.min(
                  endIndex,
                  totalEntries,
                )} of ${totalEntries} beneficiaries`
              : "Showing 0 beneficiaries"}
          </p>

          <div className="ms-pagination">
            <button
              className="ms-page-btn"
              onClick={() => goToPage(safePage - 1)}
              disabled={safePage === 1}
            >
              <FaChevronLeft />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  className={`ms-page-btn ${
                    safePage === page ? "ms-page-active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ),
            )}

            <button
              className="ms-page-btn"
              onClick={() => goToPage(safePage + 1)}
              disabled={safePage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        {/* Scheme Info Banner */}
        <div className="ms-scheme-banner">
          <div className="ms-banner-icon-left">
            <FaBus />
          </div>

          <div className="ms-banner-text">
            <h4>Maha Shakthi Scheme</h4>
            <p>
              Providing free bus travel for women across Andhra Pradesh to
              support their daily commute, education and empowerment.
            </p>
          </div>
        </div>
      </div>

      {/* ===================== EDIT MODAL ===================== */}
      {editItem && editForm && (
        <div className="ms-modal-overlay" onClick={closeEditModal}>
          <div
            className="ms-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ms-modal-header">
              <h3>Edit Beneficiary</h3>
              <button className="ms-modal-close" onClick={closeEditModal}>
                <FaTimes />
              </button>
            </div>

            <form className="ms-modal-form" onSubmit={handleEditSave}>
              <div className="ms-form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleEditChange("name", e.target.value)}
                  required
                />
              </div>

              <div className="ms-form-row">
                <label>Relation</label>
                <input
                  type="text"
                  value={editForm.relation}
                  onChange={(e) =>
                    handleEditChange("relation", e.target.value)
                  }
                  required
                />
              </div>

              <div className="ms-form-grid">
                <div className="ms-form-row">
                  <label>Bus Pass No.</label>
                  <input
                    type="text"
                    value={editForm.busPass}
                    onChange={(e) =>
                      handleEditChange("busPass", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="ms-form-row">
                  <label>Route</label>
                  <input
                    type="text"
                    value={editForm.route}
                    onChange={(e) =>
                      handleEditChange("route", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="ms-form-grid">
                <div className="ms-form-row">
                  <label>Depot</label>
                  <input
                    type="text"
                    value={editForm.depot}
                    onChange={(e) =>
                      handleEditChange("depot", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="ms-form-row">
                  <label>Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) =>
                      handleEditChange("status", e.target.value)
                    }
                  >
                    <option>Approved</option>
                    <option>Pending</option>
                  </select>
                </div>
              </div>

              <div className="ms-form-grid">
                <div className="ms-form-row">
                  <label>Village</label>
                  <input
                    type="text"
                    value={editForm.village}
                    onChange={(e) =>
                      handleEditChange("village", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="ms-form-row">
                  <label>Mandal</label>
                  <select
                    value={editForm.mandal}
                    onChange={(e) =>
                      handleEditChange("mandal", e.target.value)
                    }
                  >
                    <option>Kandukur</option>
                    <option>Gudluru</option>
                    <option>Ulavapadu</option>
                  </select>
                </div>
              </div>

              <div className="ms-form-row">
                <label>Date</label>
                <input
                  type="text"
                  placeholder="DD-MM-YYYY"
                  value={editForm.date}
                  onChange={(e) => handleEditChange("date", e.target.value)}
                  required
                />
              </div>

              <div className="ms-modal-actions">
                <button
                  type="button"
                  className="ms-modal-cancel-btn"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="ms-modal-save-btn">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMahaShakthiTable;